import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [sessionId, setSessionId] = useState(localStorage.getItem('cart_session_id') || generateSessionId());
  const { user, token } = useAuth();

  function generateSessionId() {
    const id = 'session_' + Math.random().toString(36).substr(2, 9) + Date.now();
    localStorage.setItem('cart_session_id', id);
    return id;
  }

  useEffect(() => {
    fetchCart();
  }, [user, sessionId, token]);

  const fetchCart = async () => {
    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const url = user ? `${API}/cart` : `${API}/cart?session_id=${sessionId}`;
      const response = await axios.get(url, config);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart', error);
    }
  };

  const addToCart = async (product, variantIndex = null, quantity = 1) => {
    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const data = {
        item: {
          product_id: product.id,
          variant_index: variantIndex,
          quantity
        }
      };
      if (!user) {
        data.session_id = sessionId;
      }
      const response = await axios.post(`${API}/cart`, data, config);
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Failed to add to cart', error);
      return false;
    }
  };

  const updateCart = async (items) => {
    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const data = { items };
      if (!user) {
        data.session_id = sessionId;
      }
      await axios.put(`${API}/cart`, data, config);
      await fetchCart();
    } catch (error) {
      console.error('Failed to update cart', error);
    }
  };

  const removeFromCart = async (productId, variantIndex) => {
    const updatedItems = cart.items.filter(
      item => !(item.product_id === productId && item.variant_index === variantIndex)
    );
    await updateCart(updatedItems);
  };

  const clearCart = async () => {
    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const url = user ? `${API}/cart` : `${API}/cart?session_id=${sessionId}`;
      await axios.delete(url, config);
      setCart({ items: [] });
    } catch (error) {
      console.error('Failed to clear cart', error);
    }
  };

  const cartItemsCount = cart.items?.reduce((total, item) => total + item.quantity, 0) || 0;

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateCart, clearCart, fetchCart, cartItemsCount }}>
      {children}
    </CartContext.Provider>
  );
};
